import { useEffect } from "react";
import { useSocket } from "../context/SocketContext";
import useAuth from "./useAuth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchMessageNotifications,
  fetchNotifications,
  markAllMessagesAsReadByChatroom,
  markMessageNotificationAsReadAll,
  markNotificationAsRead,
  markNotificationAsReadAll,
} from "../services/api/notifications";
import { useDispatch } from "react-redux";
import { showToast } from "../store/toast/toastSlice";
import playSound from "../utils/playSound";

export function useNotificationsSocket() {
  const dispatch = useDispatch();
  const { auth } = useAuth();
  const socket = useSocket();
  const queryClient = useQueryClient();
  const { data: messageNotification, refetch: refetchMessageNotifications } = useQuery({
    queryKey: ["messageNotifications", auth.user.id],
    queryFn: () => fetchMessageNotifications(auth.user.id),
    enabled: !!auth.user.id,
  });
  const { data: allOtherNotificationsData, refetch: refetchOtherNotifications } = useQuery({
    queryKey: ["otherNotifications", auth.user.id],
    queryFn: () => fetchNotifications(auth.user.id),
    enabled: !!auth.user.id,
  });

  const messageNotifications = messageNotification?.data?.data || [];
  const allOtherNotifications = allOtherNotificationsData?.data?.data || [];

  const messageNextCursor = messageNotification?.data?.nextCursor || null;
  const otherNextCursor = allOtherNotificationsData?.data?.nextCursor || null;

  const {
    isPending: isMarkAllMessagesAsReadByChatroomApiLoading,
    error: isMarkAllMessagesAsReadByChatroomApiError,
    data: updatedUser,
    mutateAsync: markAllMessagesAsReadByChatroomMutation,
  } = useMutation({
    mutationFn: markAllMessagesAsReadByChatroom,
    enabled: false,
  });

  const loadMoreMessageNotifications = async () => {
    if (!messageNextCursor) return;

    const more = await fetchMessageNotifications(auth.user.id, messageNextCursor, 10);
    queryClient.setQueryData(["messageNotifications", auth.user.id], (old) => {
      if (!old) return old;

      return {
        ...old,
        data: {
          ...old.data,
          data: [...old.data.data, ...more.data.data],
          nextCursor: more.data.nextCursor,
        },
      };
    });
  };
  const loadMoreOtherNotifications = async () => {
    if (!otherNextCursor) return;

    const more = await fetchNotifications(auth.user.id, otherNextCursor, 10);

    queryClient.setQueryData(["otherNotifications", auth.user.id], (old) => {
      if (!old) return old;

      return {
        ...old,
        data: {
          ...old.data,
          data: [...old.data.data, ...more.data.data],
          nextCursor: more.data.nextCursor,
        },
      };
    });
  };

  useEffect(() => {
    if (!socket) return;

    const handleNotification = (newNotification) => {
      const isMessage = newNotification.type === "MESSAGE";

      if (newNotification.type === "FREELANCE_COMPANY_INVITE" || newNotification.type === "SYSTEM") {
        queryClient.invalidateQueries({ queryKey: ["freelanceCompanyMembers"] });
        queryClient.invalidateQueries({ queryKey: ["myLinkedCompanies"] });
        queryClient.invalidateQueries({ queryKey: ["freelanceCompanyInvitations"] });
      }

      if (newNotification.type === "SYSTEM") {
        dispatch(
          showToast({
            type: "notification",
            avatar:
              newNotification?.sender?.digitalAsset?.length > 0
                ? newNotification?.sender?.digitalAsset[0]?.url
                : "",
            sender: `${newNotification?.sender?.firstName} ${newNotification?.sender?.lastName}`,
            title: "You have a new notification",
            message: newNotification.content,
            url: newNotification.url,
            time: "now",
            sound: true,
          }),
        );
      }

      const key = isMessage
        ? ["messageNotifications", auth.user.id]
        : ["otherNotifications", auth.user.id];

      queryClient.setQueryData(key, (old) => {
        const oldNotifications = old?.data?.data || [];

        if (!old) {
          const unreadCount = newNotification.isRead ? 0 : 1;
          return {
            data: {
              data: [newNotification],
              nextCursor: null,
              unreadNotificationCount: unreadCount,
            },
          };
        }
        const exists = oldNotifications.some((n) => n.id === newNotification.id);
        if (exists) return old;

        const updated = [newNotification, ...oldNotifications];

        let unreadNotificationCount = old.data?.unreadNotificationCount || 0;

        if (!newNotification.isRead) {
          if (isMessage) {
            const alreadyUnreadForWorkOrder = oldNotifications.some(
              (n) =>
                n.type === "MESSAGE" && !n.isRead && n.workOrderId === newNotification.workOrderId,
            );

            if (!alreadyUnreadForWorkOrder) {
              unreadNotificationCount += 1;
            }
          } else {
            unreadNotificationCount += 1;
          }
        }

        return {
          ...old,
          data: {
            ...old.data,
            data: updated,
            unreadNotificationCount,
          },
        };
      });
    };
    socket.off("notification");
    socket.on("notification", handleNotification);
    return () => socket.off("notification", handleNotification);
  }, [socket, auth.user.id]);

  const markAsRead = useMutation({
    mutationFn: (id) => markNotificationAsRead(id),
    onSuccess: (_, id) => {
      queryClient.setQueryData(["otherNotifications", auth.user.id], (old) => {
        if (!old) return old;
        const oldNotifications = old.data?.data || [];
        const updated = oldNotifications.map((n) => (n.id === id ? { ...n, isRead: true } : n));
        let unreadNotificationCount = old.data?.unreadNotificationCount || 0;
        const wasUnread = oldNotifications.find((n) => n.id === id && !n.isRead);
        if (wasUnread) {
          unreadNotificationCount = Math.max(unreadNotificationCount - 1, 0);
        }

        return {
          ...old,
          data: {
            ...old.data,
            data: updated,
            unreadNotificationCount,
          },
        };
      });
    },
  });

  const markAllAsRead = useMutation({
    mutationFn: () => markNotificationAsReadAll(),
    onSuccess: () => {
      queryClient.setQueryData(["otherNotifications", auth.user.id], (old) => ({
        ...old,
        data: {
          ...old.data,
          data: old.data.data.map((n) => ({ ...n, isRead: true })),
          unreadNotificationCount: 0,
        },
      }));
    },
  });

  const markAllMessagesAsRead = useMutation({
    mutationFn: () => markMessageNotificationAsReadAll(),
    onSuccess: () => {
      queryClient.setQueryData(["messageNotifications", auth.user.id], (old) => ({
        ...old,
        data: {
          ...old.data,
          data: old.data.data.map((n) => ({ ...n, isRead: true })),
          unreadNotificationCount: 0,
        },
      }));
    },
  });

  const chatNotifications =
    (Array.isArray(messageNotifications)
      ? messageNotifications
      : messageNotifications?.data || []
    ).filter((n) => n.type === "MESSAGE") || [];
  const otherNotifications =
    (Array.isArray(allOtherNotifications)
      ? allOtherNotifications
      : allOtherNotifications?.data || []
    ).filter((n) => n.type !== "MESSAGE") || [];

  const unreadNotificationCount = allOtherNotificationsData?.data?.unreadNotificationCount || 0;
  // (Array.isArray(allOtherNotifications)
  //   ? allOtherNotifications
  //   : allOtherNotifications?.data || []
  // ).filter((n) => !n.isRead && n.type !== "MESSAGE").length || 0;

  const chatNotificationsFilter = processNotificationsWithUnreadCount(chatNotifications);

  const unreadMessageCount = messageNotification?.data?.unreadNotificationCount || 0;
  // (Array.isArray(chatNotificationsFilter)
  //   ? chatNotificationsFilter
  //   : chatNotificationsFilter?.data || []
  // ).filter((n) => !n.isRead && n.type === "MESSAGE").length || 0;

  return {
    refetchMessageNotifications,
    notifications: otherNotifications,
    chatNotifications: chatNotificationsFilter,
    markAsRead,
    markAllAsRead,
    markAllMessagesAsRead,
    markAllMessagesAsReadByChatroomMutation,
    unreadNotificationCount,
    unreadMessageCount,
    loadMoreOtherNotifications,
    loadMoreMessageNotifications,
    otherNextCursor,
    messageNextCursor,
  };
}

function processNotificationsWithUnreadCount(notifications) {
  const grouped = new Map();

  for (const notification of notifications) {
    // const key = `${notification.workOrderId}_${notification.senderId}`;
    const key = `${notification.conversationId}`;

    if (!grouped.has(key)) {
      grouped.set(key, {
        latest: notification,
        unreadCount: notification.isRead ? 0 : 1,
      });
    } else {
      const entry = grouped.get(key);

      // Update unread count
      if (!notification.isRead) {
        entry.unreadCount += 1;
      }

      // Replace with more recent notification
      if (new Date(notification.createdAt) > new Date(entry.latest.createdAt)) {
        entry.latest = notification;
      }

      grouped.set(key, entry);
    }
  }

  // Convert map to array
  return Array.from(grouped.values()).map(({ latest, unreadCount }) => ({
    ...latest,
    unreadCount,
  }));
}
